import { getTheme, getFeatureEnabled, isSweepstakesQuery, getTrackingService, setTheme, FeatureEnabledV2, Throttle, ValidationGuard, MagicConfigMixin } from '@gig/magic-common';
import { __extends, __awaiter, __generator, __assign, __decorate, __metadata } from 'tslib';
import { Prop, Emit, Watch, Component, Mixins } from 'vue-property-decorator';
import { magic, mapToAmlStatus } from '@gig/magic-sdk';
import { Feature, AML_STATUS_QUERY, USER_PROFILE_DATA, COREX_AML_FILES_QUERY, UPDATE_USER_STATUS } from '@gig/magic-messages';
import { store } from '../../store/index.js';
import { validationMixin } from 'vuelidate';
import { extractValuesOnly } from '@gig/magic-form-builder';
import { notificationService, NotificationStyle } from '@gig/magic-messaging';
import { DocumentType, AmlStatus } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';
import { uploadFile } from '../../sdk/file-upload-sdk.js';
import { getCategoryByDocumentType } from '../verification-upload-form/form-config/document-type.config.js';

/**
 * Shows user profile information and a form to update it.
 * Allows to change email address, password and phone number.
 */
var ProfileComponent = /** @class */ (function (_super) {
    __extends(ProfileComponent, _super);
    function ProfileComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.isIdScanEnabled = false;
        _this.alwaysAllowDocumentUpload = false;
        _this.proofOfIdOnProfile = false;
        _this.userDocuments = [];
        _this.isLoading = false;
        _this.form = {};
        /**
         * Whether or not the profile has been locally edited by the user
         * without changing the profile
         */
        _this.profileModified = false;
        _this.isThemeDark = getTheme() === 'dark';
        _this.isSweepstakes = false;
        _this.showVerificationPOI = false;
        _this.amlStatus = null;
        _this.user = magic.session.getUser();
        _this.isScanError = false;
        _this.scanIdVerified = false;
        return _this;
    }
    ProfileComponent.prototype.created = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = this;
                        return [4 /*yield*/, isSweepstakesQuery()];
                    case 1:
                        _a.isSweepstakes = _c.sent();
                        _b = this;
                        return [4 /*yield*/, getFeatureEnabled(Feature.PROOF_OF_ID_ON_PROFILE)];
                    case 2:
                        _b.proofOfIdOnProfile = _c.sent();
                        if (!this.proofOfIdOnProfile) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.checkPOI()];
                    case 3:
                        _c.sent();
                        _c.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(ProfileComponent.prototype, "customerName", {
        get: function () {
            var _a, _b;
            var data = {
                firstName: (_a = this.user) === null || _a === void 0 ? void 0 : _a.firstName,
                lastName: (_b = this.user) === null || _b === void 0 ? void 0 : _b.lastName,
            };
            var translation = this.$t('profile.customer-name', data);
            if (translation) {
                return translation;
            }
            // Backwards compatibility we return the profile name
            return data.firstName + " " + data.lastName;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ProfileComponent.prototype, "isFormInvalidOrUnchanged", {
        get: function () {
            if (!this.profileModified ||
                Object.values(this.form).some(function (field) { return (field === null || field === void 0 ? void 0 : field.isValid) === false; })) {
                return true;
            }
            return false;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ProfileComponent.prototype, "documentPOI", {
        get: function () {
            var _a, _b;
            var DOCUMENT_POI = {
                type: DocumentType.PHOTO_ID,
                hasIdSelector: true,
                key: 'verification.id-proof',
                id: 'verification-upload-photo-id',
            };
            if (((_a = this.amlStatus) === null || _a === void 0 ? void 0 : _a.status[DocumentType.PHOTO_ID]) !== AmlStatus.NOT_SET) {
                var filteredFiles = [];
                if (this.userDocuments) {
                    filteredFiles = this.userDocuments
                        .filter(function (file) {
                        return (file.category === getCategoryByDocumentType(DOCUMENT_POI.type));
                    })
                        .map(function (file) {
                        // default data is not used e.g: kycId, statusId etc...
                        return {
                            kycId: 0,
                            statusId: 0,
                            statusMessage: '',
                            reasonText: '',
                            dateUploaded: file.createdOn,
                            helpText: '',
                            sowSupportDocType: '',
                            statusText: mapToAmlStatus(file.verificationStatus),
                            fileName: file.fileName,
                        };
                    });
                }
                return __assign(__assign({}, DOCUMENT_POI), { files: filteredFiles, status: (_b = this.amlStatus) === null || _b === void 0 ? void 0 : _b.status[DOCUMENT_POI.type] });
            }
            return __assign(__assign({}, DOCUMENT_POI), { files: [], status: null, uploadAllowed: false });
        },
        enumerable: false,
        configurable: true
    });
    ProfileComponent.prototype.checkPOI = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a, givenName, familyName, birthPlace, nationality, gender, _b, streetAddress, addressLocality, addressRegion, addressCountry, postalCode, sourceOfIncome, jobTitle, urlParams, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0: return [4 /*yield*/, window.queryBus.query(USER_PROFILE_DATA)];
                    case 1:
                        _a = (_d.sent()).user, givenName = _a.givenName, familyName = _a.familyName, birthPlace = _a.birthPlace, nationality = _a.nationality, gender = _a.gender, _b = _a.address, streetAddress = _b.streetAddress, addressLocality = _b.addressLocality, addressRegion = _b.addressRegion, addressCountry = _b.addressCountry, postalCode = _b.postalCode, sourceOfIncome = _a.sourceOfIncome, jobTitle = _a.jobTitle;
                        if (!(givenName &&
                            familyName &&
                            birthPlace &&
                            nationality &&
                            gender &&
                            streetAddress &&
                            addressLocality &&
                            addressRegion &&
                            addressCountry &&
                            postalCode &&
                            sourceOfIncome &&
                            jobTitle)) {
                            return [2 /*return*/];
                        }
                        urlParams = new URLSearchParams(window.location.search);
                        if (urlParams.get('scan-verification') === 'complete') {
                            this.scanIdVerified = true;
                        }
                        return [4 /*yield*/, this.fetchUserDocuments()];
                    case 2:
                        _d.sent();
                        _c = this;
                        return [4 /*yield*/, window.queryBus.query(AML_STATUS_QUERY)];
                    case 3:
                        _c.amlStatus = _d.sent();
                        this.showVerificationPOI =
                            ![AmlStatus.APPROVED, AmlStatus.NOT_SET].includes(this.amlStatus.status[DocumentType.PHOTO_ID]);
                        return [2 /*return*/];
                }
            });
        });
    };
    ProfileComponent.prototype.extractValuesOnly = function (form) {
        var valuesOnly = {};
        Object.entries(form).forEach(function (_a) {
            var key = _a[0], field = _a[1];
            valuesOnly[key] = field.value;
        });
        return valuesOnly;
    };
    ProfileComponent.prototype.onScanError = function () {
        this.isScanError = true;
        this.onError();
    };
    ProfileComponent.prototype.onError = function () {
        if (this.isScanError) {
            notificationService.show(this.$t('file-upload.scan.failed'), {
                style: NotificationStyle.ERROR,
                uniqueID: 'failed-scan-error',
            });
            this.isScanError = false;
        }
        else {
            notificationService.show(this.$t('verification.upload-error'), {
                style: NotificationStyle.ERROR,
                uniqueID: 'verification-upload-error',
            });
        }
    };
    ProfileComponent.prototype.fileSelected = function (params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var idDocType, requestedDocumentId, e_1;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!params.uploadFormValue) {
                            return [2 /*return*/];
                        }
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, , 4]);
                        idDocType = params.uploadFormValue.type;
                        requestedDocumentId = (_b = (_a = this.userDocuments) === null || _a === void 0 ? void 0 : _a.find(function (doc) {
                            return doc.category === getCategoryByDocumentType(DocumentType.PHOTO_ID) &&
                                doc.verificationStatus === AmlStatus.REQUESTED;
                        })) === null || _b === void 0 ? void 0 : _b.id;
                        return [4 /*yield*/, uploadFile({
                                file: params.file,
                                additionalParams: __assign(__assign({}, params.uploadFormValue), { Id: requestedDocumentId }),
                                onProgressChange: params.onProgressChange,
                            })];
                    case 2:
                        _c.sent();
                        this.onFileUploaded(DocumentType.PHOTO_ID, idDocType);
                        notificationService.show(this.$t('verification.upload-success'), {
                            style: NotificationStyle.SUCCESS,
                            uniqueID: 'verification-upload-success',
                        });
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _c.sent();
                        this.onError();
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    ProfileComponent.prototype.onFileUploaded = function (docType, idDocType) {
        if (idDocType === void 0) { idDocType = null; }
        return {
            docType: docType,
            idDocType: idDocType,
        };
    };
    ProfileComponent.prototype.fetchUserDocuments = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this;
                        return [4 /*yield*/, window.queryBus.query(COREX_AML_FILES_QUERY)];
                    case 1:
                        _a.userDocuments = _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Handler for each change event to the for builder state.
     * @param event
     * @returns
     */
    ProfileComponent.prototype.onChange = function (event) {
        var newForm = event.detail[0].form;
        var newValues = extractValuesOnly(newForm);
        var initialValues = extractValuesOnly(this.initialState || {});
        this.profileModified = Object.keys(newValues).some(function (key) {
            return !newForm[key].hidden && newValues[key] !== initialValues[key];
        });
        this.form = newForm;
    };
    ProfileComponent.prototype.onSubmit = function () {
        return __awaiter(this, void 0, void 0, function () {
            var submittedData;
            return __generator(this, function (_a) {
                if (this.isFormInvalidOrUnchanged) {
                    return [2 /*return*/];
                }
                submittedData = extractValuesOnly(this.form);
                this.$emit('submitData', submittedData);
                return [2 /*return*/];
            });
        });
    };
    ProfileComponent.prototype.updateUserProfile = function (submittedData) {
        return __awaiter(this, void 0, void 0, function () {
            var e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!submittedData)
                            return [2 /*return*/];
                        this.isLoading = true;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 6, 7, 8]);
                        return [4 /*yield*/, magic.methods.users.updateUserProfile(submittedData)];
                    case 2:
                        _a.sent();
                        getTrackingService().trackUpdateProfile();
                        notificationService.show(this.$t('profile.updated.notification.text'), { uniqueID: 'profile-updated-notification-text' });
                        return [4 /*yield*/, window.commandBus.publish(UPDATE_USER_STATUS)];
                    case 3:
                        _a.sent();
                        if (!this.proofOfIdOnProfile) return [3 /*break*/, 5];
                        return [4 /*yield*/, this.checkPOI()];
                    case 4:
                        _a.sent();
                        _a.label = 5;
                    case 5: return [3 /*break*/, 8];
                    case 6:
                        e_2 = _a.sent();
                        notificationService.show(this.$t('error.generic'), {
                            uniqueID: 'profile-updated-error-generic',
                        });
                        return [3 /*break*/, 8];
                    case 7:
                        this.isLoading = false;
                        this.profileModified = false;
                        return [7 /*endfinally*/];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    ProfileComponent.prototype.setTheme = function () {
        if (this.isThemeDark) {
            setTheme('dark');
        }
        else {
            setTheme('light');
        }
    };
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], ProfileComponent.prototype, "showUsername", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], ProfileComponent.prototype, "showName", void 0);
    __decorate([
        Prop(Boolean),
        __metadata("design:type", Boolean)
    ], ProfileComponent.prototype, "showTheming", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Object)
    ], ProfileComponent.prototype, "initialState", void 0);
    __decorate([
        Prop(),
        __metadata("design:type", Array)
    ], ProfileComponent.prototype, "formConfig", void 0);
    __decorate([
        Prop({ type: Object }),
        __metadata("design:type", Object)
    ], ProfileComponent.prototype, "submittedData", void 0);
    __decorate([
        FeatureEnabledV2({
            slug: Feature.SCAN_VERIFY_FLOW,
        }),
        __metadata("design:type", Object)
    ], ProfileComponent.prototype, "isIdScanEnabled", void 0);
    __decorate([
        FeatureEnabledV2({
            slug: Feature.ALWAYS_ALLOW_DOCUMENT_UPLOAD,
        }),
        __metadata("design:type", Object)
    ], ProfileComponent.prototype, "alwaysAllowDocumentUpload", void 0);
    __decorate([
        Emit('fileUploaded'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String, Object]),
        __metadata("design:returntype", Object)
    ], ProfileComponent.prototype, "onFileUploaded", null);
    __decorate([
        Throttle(500),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [CustomEvent]),
        __metadata("design:returntype", void 0)
    ], ProfileComponent.prototype, "onChange", null);
    __decorate([
        ValidationGuard(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", Promise)
    ], ProfileComponent.prototype, "onSubmit", null);
    __decorate([
        Watch('submittedData'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [Object]),
        __metadata("design:returntype", Promise)
    ], ProfileComponent.prototype, "updateUserProfile", null);
    ProfileComponent = __decorate([
        Component({
            store: store,
            name: 'corex-profile',
        })
    ], ProfileComponent);
    return ProfileComponent;
}(Mixins(validationMixin, MagicConfigMixin)));

export default ProfileComponent;
